import {
  describirFrecuencia,
  FRECUENCIA_DIARIA,
  formatearHora,
  formatearRevision,
  NOMBRE_ESTADO_HOJA,
} from "@gestiondoc/shared";
import { useQuery } from "@tanstack/react-query";
import { Link, useRouteContext } from "@tanstack/react-router";
import { useState } from "react";
import { useTRPC } from "../api.ts";
import { Encabezado } from "../componentes/Encabezado.tsx";
import { Aviso, BotonEnlace, EstadoVacio, Pildora, PildoraDeHoja } from "../componentes/ui.tsx";
import { useAbrirHoja } from "../componentes/useAbrirHoja.ts";
import type { MisDiligencias } from "../tipos.ts";

/**
 * Fase 3b · "Diligencias": lo que esta persona llena. Arriba, los formatos que **tocan hoy** según
 * la frecuencia de cada asignación (todos los días, los lunes, el día 5…; GES-REG-022…) con un
 * botón grande para llenarlos, y "Otra hoja" para los que se llenan varias veces al día, una por
 * báscula o por turno; después, los asignados que no tocan hoy, con su próximo día; abajo, lo
 * que llenó los días anteriores, por fecha. Sin firmas: cada hoja queda con quién y cuándo.
 */

/** "martes 23 de septiembre". Las fechas de las hojas son días (medianoche UTC). */
const fmtDiaLargo = new Intl.DateTimeFormat("es-HN", {
  timeZone: "UTC",
  weekday: "long",
  day: "numeric",
  month: "long",
});

function diaLargo(d: Date, hoy: Date): string {
  const dias = Math.round((hoy.getTime() - d.getTime()) / 86_400_000);
  const texto = fmtDiaLargo.format(d);
  if (dias === 0) return `Hoy, ${texto}`;
  if (dias === 1) return `Ayer, ${texto}`;
  if (dias === -1) return `Mañana, ${texto}`;
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

export function PaginaDiligencias() {
  const trpc = useTRPC();
  const { perfil } = useRouteContext({ from: "/app" });
  // Siempre fresca al entrar: se vuelve aquí desde una hoja recién llenada.
  const q = useQuery({ ...trpc.hojas.diligencias.queryOptions(), refetchOnMount: "always" });
  if (q.isPending) return <EstadoVacio>Cargando…</EstadoVacio>;
  if (q.isError) return <EstadoVacio>{q.error.message}</EstadoVacio>;
  const d = q.data;
  // Hoy toca lo que dice su frecuencia; y un formato que ya tiene hoja de hoy se queda arriba.
  const deHoy = d.formatos.filter((f) => f.tocaHoy || f.hojas.length > 0);
  const otrosDias = d.formatos.filter((f) => !deHoy.includes(f));
  const hechas = deHoy.filter((f) => f.hojas.length).length;
  const otras = otrasPorFormato(d.otrasDeHoy);

  return (
    <div className="max-w-[980px]">
      <Encabezado
        overline="Del día"
        titulo="Diligencias"
        acciones={
          perfil.usuario.rolGlobal === "administrador" && (
            <BotonEnlace to="/formatos/cargar">+ Cargar formato existente</BotonEnlace>
          )
        }
      />

      <section className="mb-8">
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="font-display text-[17px] font-bold text-marino">
            {diaLargo(d.hoy, d.hoy)}
          </h2>
          {deHoy.length > 0 && (
            <span className="text-[13px] text-texto-3">
              {hechas} de {deHoy.length} empezada{deHoy.length === 1 ? "" : "s"}
            </span>
          )}
        </div>
        {d.formatos.length === 0 && otras.length === 0 ? (
          <EstadoVacio>No tienes formatos asignados en tus diligencias.</EstadoVacio>
        ) : deHoy.length === 0 && otras.length === 0 ? (
          <EstadoVacio>Hoy no te toca ningún formato.</EstadoVacio>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {deHoy.map((f) => (
              <TarjetaDeHoy key={f.codigo} formato={f} />
            ))}
            {otras.map((f) => (
              <TarjetaDeHoy key={f.codigo} formato={f} />
            ))}
          </div>
        )}
      </section>

      {otrosDias.length > 0 && (
        <section className="mb-8">
          <h2 className="mb-1 font-display text-[17px] font-bold text-marino">Otros días</h2>
          <p className="mb-3 text-[13px] text-texto-3">
            Formatos que tienes asignados pero que hoy no tocan. Si hace falta, igual puedes llenar
            una hoja hoy.
          </p>
          <ul className="divide-y divide-borde/60 rounded-tarjeta border border-borde bg-white">
            {otrosDias.map((f) => (
              <FilaDeOtroDia key={f.codigo} formato={f} hoy={d.hoy} />
            ))}
          </ul>
        </section>
      )}

      {d.porVerificar.length > 0 && (
        <section className="mb-8">
          <h2 className="mb-1 font-display text-[17px] font-bold text-marino">
            {NOMBRE_ESTADO_HOJA.por_verificar}
          </h2>
          <p className="mb-3 text-[13px] text-texto-3">
            Hojas con no aceptables: escribe la verificación en el sitio sobre la misma hoja y pulsa
            "Verificación hecha".
          </p>
          <ul className="divide-y divide-borde/60 rounded-tarjeta border border-rojo/30 bg-white">
            {d.porVerificar.map((h) => (
              <li key={h.id}>
                <Link
                  to="/hojas/$id"
                  params={{ id: h.id }}
                  className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 hover:bg-fondo"
                >
                  <span className="font-mono text-[12px] text-marino">{h.codigo}</span>
                  <span className="min-w-0 flex-1 font-semibold">{h.nombre}</span>
                  <span className="text-[12px] text-texto-3">{diaLargo(h.fecha, d.hoy)}</span>
                  <span className="text-[12.5px] font-semibold text-marino">Verificar →</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <h2 className="mb-3 font-display text-[17px] font-bold text-marino">Días anteriores</h2>
        {d.anteriores.length === 0 ? (
          <EstadoVacio>Todavía no hay hojas de días anteriores.</EstadoVacio>
        ) : (
          <div className="space-y-4">
            {porDia(d.anteriores).map(([clave, hojas]) => (
              <div key={clave} className="rounded-tarjeta border border-borde bg-white">
                <div className="border-b border-borde/70 px-4 py-2.5 text-[13px] font-semibold text-marino">
                  {diaLargo(hojas[0]?.fecha ?? d.hoy, d.hoy)}
                </div>
                <ul className="divide-y divide-borde/60">
                  {hojas.map((h) => (
                    <li key={h.id}>
                      <Link
                        to="/hojas/$id"
                        params={{ id: h.id }}
                        className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 hover:bg-fondo"
                      >
                        <span className="font-mono text-[12px] text-marino">{h.codigo}</span>
                        <span className="min-w-0 flex-1 font-semibold">{h.nombre}</span>
                        <span className="text-[12px] text-texto-3">
                          Abierta {formatearHora(h.creadaEn)} ·{" "}
                          {h.trazos
                            ? `${h.trazos} trazo${h.trazos === 1 ? "" : "s"}`
                            : h.conPersonal
                              ? "personal puesto"
                              : "en blanco"}
                        </span>
                        <PildoraDeHoja estado={h.estado} resultado={h.resultado} />
                        <span className="text-[12.5px] font-semibold text-marino">Ver →</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

type FormatoDeHoy = MisDiligencias["formatos"][number];

/** Las hojas de hoy de formatos que no tengo asignados, juntas por formato como una tarjeta. */
function otrasPorFormato(hojas: MisDiligencias["otrasDeHoy"]): FormatoDeHoy[] {
  const porCodigo = new Map<string, FormatoDeHoy>();
  for (const { codigo, nombre, resultado: _, ...h } of hojas) {
    const f = porCodigo.get(codigo);
    if (f) f.hojas.push(h);
    else
      porCodigo.set(codigo, {
        codigo,
        nombre,
        proceso: "",
        revision: null,
        frecuencia: FRECUENCIA_DIARIA,
        tocaHoy: true,
        proximoDia: h.creadaEn,
        seLlena: true,
        hojas: [h],
      });
  }
  return [...porCodigo.values()];
}

/** Un formato asignado que hoy no toca: cuándo le toca, y la puerta para llenarlo igual. */
function FilaDeOtroDia({ formato: f, hoy }: { formato: FormatoDeHoy; hoy: Date }) {
  const [error, setError] = useState<string | null>(null);
  const abrir = useAbrirHoja(setError);
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3">
      <span className="font-mono text-[12px] text-marino">{f.codigo}</span>
      <span className="min-w-0 flex-1 font-semibold">{f.nombre}</span>
      <span className="text-[12px] text-texto-3">
        {describirFrecuencia(f.frecuencia)} · próxima: {diaLargo(f.proximoDia, hoy)}
      </span>
      {f.seLlena && (
        <button
          type="button"
          disabled={abrir.isPending}
          onClick={() => abrir.mutate({ codigo: f.codigo })}
          className="text-[12.5px] font-semibold text-marino hover:underline disabled:opacity-60"
        >
          {abrir.isPending ? "Abriendo…" : "Llenar hoy →"}
        </button>
      )}
      {error && <Aviso className="w-full">{error}</Aviso>}
    </li>
  );
}

function TarjetaDeHoy({ formato: f }: { formato: FormatoDeHoy }) {
  const [error, setError] = useState<string | null>(null);
  const abrir = useAbrirHoja(setError);
  const empezadas = f.hojas;
  const clase =
    "mt-3 flex min-h-[48px] w-full items-center justify-center rounded-lg px-4 text-[14px] font-bold";

  return (
    <div
      className={`rounded-tarjeta border bg-white p-4 ${empezadas.length ? "border-verde/40" : "border-borde"}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-mono text-[12px] text-marino">
            {f.codigo}
            {f.revision && ` · Rev. ${formatearRevision(f.revision.numero)}`}
          </div>
          <div className="text-[15px] leading-snug font-semibold">{f.nombre}</div>
          {(f.proceso || f.frecuencia.tipo !== "diaria") && (
            <div className="text-[12px] text-texto-3">
              {[f.proceso, f.frecuencia.tipo === "diaria" ? "" : describirFrecuencia(f.frecuencia)]
                .filter(Boolean)
                .join(" · ")}
            </div>
          )}
        </div>
        {empezadas.some((h) => h.estado === "por_verificar") ? (
          <Pildora tono="bg-rojo-fondo text-rojo">{NOMBRE_ESTADO_HOJA.por_verificar}</Pildora>
        ) : empezadas.length && empezadas.every((h) => h.estado === "cerrada") ? (
          <Pildora tono="bg-verde-fondo text-verde">Terminada</Pildora>
        ) : empezadas.length ? (
          <Pildora tono="bg-verde-fondo text-verde">Empezada</Pildora>
        ) : (
          <Pildora tono="bg-ambar-fondo text-ambar">Pendiente</Pildora>
        )}
      </div>
      {empezadas.length > 0 && (
        <ul className="mt-3 divide-y divide-borde/60 rounded-lg border border-borde/70">
          {empezadas.map((h) => (
            <li key={h.id}>
              <Link
                to="/hojas/$id"
                params={{ id: h.id }}
                className="flex min-h-[48px] items-center gap-3 px-3 py-2 hover:bg-fondo"
              >
                <span className="font-mono text-[13px] font-semibold text-marino">
                  {formatearHora(h.creadaEn)}
                </span>
                <span className="min-w-0 flex-1 text-[12.5px] text-texto-3">
                  {h.trazos
                    ? `${h.trazos} trazo${h.trazos === 1 ? "" : "s"} · guardada ${formatearHora(h.actualizadaEn)}`
                    : h.conPersonal
                      ? `Personal puesto · guardada ${formatearHora(h.actualizadaEn)}`
                      : "Todavía en blanco"}
                </span>
                <span className="text-[12.5px] font-semibold text-marino">
                  {h.estado === "abierta" ? "Seguir →" : "Ver →"}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
      {error && <Aviso className="mt-2">{error}</Aviso>}
      {!f.seLlena ? (
        <p className="mt-3 text-[12.5px] text-texto-3">
          El archivo del formato todavía se está convirtiendo a PDF; en un momento se podrá llenar.
        </p>
      ) : empezadas.length ? (
        <button
          type="button"
          onClick={() => abrir.mutate({ codigo: f.codigo, otra: true })}
          disabled={abrir.isPending}
          className={`${clase} border border-marino text-marino hover:bg-fondo disabled:opacity-60`}
        >
          {abrir.isPending ? "Abriendo…" : "+ Otra hoja"}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => abrir.mutate({ codigo: f.codigo })}
          disabled={abrir.isPending}
          className={`${clase} bg-marino text-white hover:brightness-110 disabled:opacity-60`}
        >
          {abrir.isPending ? "Abriendo…" : "Llenar la de hoy"}
        </button>
      )}
    </div>
  );
}

function porDia(hojas: MisDiligencias["anteriores"]): [string, MisDiligencias["anteriores"]][] {
  const grupos = new Map<string, MisDiligencias["anteriores"]>();
  for (const h of hojas) {
    const clave = h.fecha.toISOString().slice(0, 10);
    const lista = grupos.get(clave);
    if (lista) lista.push(h);
    else grupos.set(clave, [h]);
  }
  return [...grupos];
}
